import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell } from "@/components/PageShell";
import { PromiseBand } from "@/components/PromiseBand";
import { aestheticTreatments } from "@/lib/aesthetics-data";
import { absUrl, BRAND, breadcrumbJsonLd } from "@/lib/seo";
import { contact, locations } from "@/lib/healic-data";
import { locationHref } from "@/lib/location-routes";
import {
  ArrowRight,
  Phone,
  Sparkles,
  ShieldCheck,
  Clock,
  Wallet,
  Stethoscope,
  Lock,
  Check,
} from "lucide-react";

const TITLE = "Aesthetics & Skin Clinic — Laser Hair Reduction | Healic";
const DESCRIPTION =
  "Dermatologist-led aesthetic treatments at Healic Multispeciality Hospital — laser hair reduction, medi-facials, peels, acne scars, PRP hair, anti-ageing and pigmentation care.";

export const Route = createFileRoute("/aesthetics/")({
  head: () => {
    const url = absUrl("/aesthetics");
    return {
      meta: [
        { title: TITLE },
        { name: "description", content: DESCRIPTION },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
        { property: "og:title", content: `Aesthetics & Skin Clinic — ${BRAND}` },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_IN" },
        { property: "og:site_name", content: BRAND },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Aesthetics & Skin Clinic — Healic" },
        { name: "twitter:description", content: DESCRIPTION },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalSpecialty",
            name: "Aesthetic & Cosmetic Dermatology",
            description: DESCRIPTION,
            relevantSpecialty: aestheticTreatments.map((t) => t.name),
            url,
          }),
        },
        breadcrumbJsonLd([
          { name: "Home", url: absUrl("/") },
          { name: "Aesthetics", url },
        ]),
      ],
    };
  },
  component: AestheticsIndex,
});

const promises = [
  { icon: Stethoscope, title: "Dermatologist-led", text: "Every plan is assessed and supervised by a qualified doctor, not a salon therapist." },
  { icon: ShieldCheck, title: "Skin-safe protocols", text: "Devices and settings chosen for Indian skin tones, with a patch test first." },
  { icon: Wallet, title: "Transparent packages", text: "Session-wise and package pricing shared upfront — no hidden add-ons." },
  { icon: Lock, title: "Complete privacy", text: "Private treatment rooms and female staff available on request." },
];

function AestheticsIndex() {
  return (
    <PageShell>
      {/* Hero */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 lg:grid-cols-[1.1fr_0.9fr] lg:px-8 lg:py-20">
          <div>
            <span className="inline-flex items-center gap-2 rounded-pill bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide">
              <Sparkles className="h-3.5 w-3.5 text-accent" /> Aesthetics at Healic
            </span>
            <h1 className="mt-5 font-display text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">
              Skin, hair &amp; laser aesthetics — led by doctors
            </h1>
            <p className="mt-4 max-w-xl text-sm opacity-90 sm:text-base">
              From laser hair reduction to acne scars, pigmentation, PRP hair therapy
              and anti-ageing — medical-grade treatments planned around your skin type,
              delivered inside a full multispeciality hospital.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/aesthetics/$slug"
                params={{ slug: "laser-hair-reduction" }}
                className="inline-flex items-center gap-2 rounded-pill bg-accent px-6 py-3 text-sm font-semibold text-accent-foreground"
              >
                Explore Laser Hair Reduction <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={`tel:${contact.phoneTel}`}
                className="inline-flex items-center gap-2 rounded-pill bg-white/10 px-6 py-3 text-sm font-semibold hover:bg-white/15"
              >
                <Phone className="h-4 w-4 text-accent" /> {contact.phone}
              </a>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 self-center">
            {promises.map((p) => (
              <div key={p.title} className="rounded-2xl bg-white/10 p-4">
                <p.icon className="h-5 w-5 text-accent" />
                <h2 className="mt-3 font-display text-sm font-semibold">{p.title}</h2>
                <p className="mt-1 text-xs opacity-80">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PromiseBand />

      {/* Treatments grid */}
      <section className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-16">
        <h2 className="font-display text-2xl font-bold sm:text-3xl">Our aesthetic treatments</h2>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Each treatment starts with a skin or scalp assessment so you get a plan that
          actually fits your concern, skin type and budget.
        </p>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {aestheticTreatments.map((t) => (
            <Link
              key={t.slug}
              to="/aesthetics/$slug"
              params={{ slug: t.slug }}
              className="group flex flex-col rounded-2xl border border-border bg-card p-5 transition hover:border-accent hover:shadow-lg"
            >
              <h3 className="font-display text-base font-semibold text-foreground">{t.name}</h3>
              <p className="mt-1 text-xs font-medium text-accent">{t.tagline}</p>
              <p className="mt-3 flex-1 text-sm text-muted-foreground">{t.short}</p>
              <div className="mt-4 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                <span className="rounded-pill bg-muted px-2.5 py-1">
                  <Clock className="mr-1 inline h-3 w-3" />
                  {t.duration}
                </span>
                <span className="rounded-pill bg-muted px-2.5 py-1">{t.sessions}</span>
              </div>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-accent">
                View details <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Why Healic */}
      <section className="bg-muted/40 py-12 lg:py-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <h2 className="font-display text-2xl font-bold sm:text-3xl">Why choose Healic for aesthetics</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              "Qualified dermatologists and cosmetologists, not walk-in salon staff",
              "Patch test and skin-type mapping before your first full session",
              "Hospital-grade hygiene, sterilisation and single-use consumables",
              "Hormonal causes like PCOS reviewed with our gynaecology team",
              "Session-wise or package pricing shared before you start",
              "Private treatment rooms with female staff on request",
              "Photo-documented progress reviews at every milestone",
              "Available across all Healic hospital locations",
            ].map((x) => (
              <div key={x} className="flex gap-2 rounded-2xl bg-card p-4 text-sm">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                <span>{x}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Locations + CTA */}
      <section className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-16">
        <div className="rounded-3xl bg-primary px-6 py-10 text-primary-foreground lg:px-12">
          <h2 className="font-display text-2xl font-bold sm:text-3xl">Book your skin &amp; laser consultation</h2>
          <p className="mt-2 max-w-2xl text-sm opacity-90">
            Talk to our aesthetic team about the right treatment, number of sessions and
            package price for your concern.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/contact" className="rounded-pill bg-accent px-6 py-3 text-sm font-semibold text-accent-foreground">
              Book a consultation
            </Link>
            <a href={`tel:${contact.phoneTel}`} className="rounded-pill bg-white/10 px-6 py-3 text-sm font-semibold hover:bg-white/15">
              Call {contact.phone}
            </a>
          </div>
          <div className="mt-8 flex flex-wrap gap-2 text-sm">
            {locations.map((l) => (
              <Link key={l.slug} to={locationHref(l.slug)} className="rounded-pill bg-white/10 px-4 py-2 hover:bg-white/15">
                {l.city}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
